import { Injectable } from "@angular/core";
import { InfinityUtil, forceAdd, toCurrency } from 'src/infinity-ui'; // { get }

const TRANSACTION_NUMBER_VALUE = "transactionNumber.value";
const CREDIT_CARD_PAYMENT_TYPE_ID = 2;
const ACH_PAYMENT_TYPE_ID = 1;
const FORM_VALIDATION_PAYMENT_TYPES = "formValidationResponse.paymentTypes";

@Injectable({ providedIn: "root" })
export class ModelService {

    constructor(public util: InfinityUtil) { }

    /**
     * [Usage]
     * Requests | this.util.get(requestModel)               | Creates request object from store values.
     * Props    | this.util.set(this.util.get(propsModel))  | Gets prop values from response in store, then stores props.
     * */

    /* ----- Root ----- */

    public programOpenProps = {
        supportEmail: "programOpenResponse.supportEmail"
    }

    public opsApiStatusProps = {
        opsApiAvailable: "opsApiStatusResponse"
    }

    public applicationFormProps = {
        // Application
        states: "applicationFormResponse.states",
        countries: "applicationFormResponse.countries",
        participant: "applicationFormResponse.participant",
        renewal: "applicationFormResponse.renewal",
        programEnrollmentContents: "applicationFormResponse.programEnrollmentContents",
        allowCreditCard: ["applicationFormResponse.paymentTypes", e => !!(e||[]).filter(type => type.paymentTypeId === 2).length],
        allowAch: ["applicationFormResponse.paymentTypes", e => !!(e||[]).filter(type => type.paymentTypeId === 1).length],
        // ProgramEnrollment
        programEnrollmentId: "applicationFormResponse.programEnrollment.programEnrollmentId",
        programId: "applicationFormResponse.programEnrollment.programId",
        ratingGroups: ["applicationFormResponse.programEnrollment.programEnrollmentRatingGroups", e => {
            const { supportEmail } = this.util.store;
            if (!e || !e.length) this.util.setItemValue("error", `<h2>No Data</h2>
                <p>It looks like there was no data (rating groups) received from the backend. This will prevent the application from submitting successfully.</p>
                <p>If you continue to receive this message, please contact us at <a href='mailto:${supportEmail}'>${supportEmail}</a>.</p>`);
            return e;
        }],
        groupRules: "applicationFormResponse.programEnrollment.programEnrollmentRatingGroupAssociationRules",
        itemRules: "applicationFormResponse.programEnrollment.programEnrollmentRatingItemAssociationRules",
        renewalDays: "applicationFormResponse.programEnrollment.renewalDays",
        // Program
        applicationCode: "applicationFormResponse.programEnrollment.program.applicationCode",
        epicCode: "applicationFormResponse.programEnrollment.program.epicCode",
        structureCode: "applicationFormResponse.programEnrollment.program.structureCode",
        showLandingPage: "applicationFormResponse.programEnrollment.program.showLandingPage"
    }

    public participantRatingSummaryProps = {
        participant: "participantRatingSummaryResponse.participant",
        participantRatingId: "participantRatingSummaryResponse.participantRatingId",
        isReferral: "participantRatingSummaryResponse.referralResponse.isReferral",
        itemSummaries: "participantRatingSummaryResponse.itemSummaries",
        // Payment
        allowPremiumFinance: "participantRatingSummaryResponse.allowPremiumFinance",
        premium: ["participantRatingSummaryResponse.premium", e => forceAdd(e, this.util.get("participantRatingSummaryResponse.premiumAdjustment"))],
        adjustedPremium: "participantRatingSummaryResponse.adjustedPremium",
        total: "participantRatingSummaryResponse.total",
        agencyFee: "participantRatingSummaryResponse.agencyFee",
        taxes: "participantRatingSummaryResponse.adjustedTaxes",
        fees: "participantRatingSummaryResponse.adjustedFees",
        surcharge: "participantRatingSummaryResponse.surcharge",
        surchargeType: ["participantRatingSummaryResponse.surchargeType", e => ([
            null,
            "NJ PLIGA Surcharge" // "CFEE"
        ])[e || 0]],
        paymentAmount: ["participantRatingSummaryResponse.total", e => {
            var adjustedPremium = this.util.get("participantRatingSummaryResponse.adjustedPremium") || 0;
            if (adjustedPremium > e) e = adjustedPremium;
            return this.setItemValue("paymentAmount", toCurrency(e));
        }],
        //returnToLanding: ["participantRatingSummaryResponse.participant", e => {
        //    const error = this.util.get("error");
        //    if (error && error.value) return;
        //    if(!e) window.location.href = "/ata";
        //}]
    }

    /* ----- Landing ----- */

    public verifyEmailRequest = {
        ApplicationCode: "applicationCode",
        ProgramId: "programId",
        Email: "verifyEmail.value"
    }

    public verifyEmailProps = {
        verificationStatus: ["verifyEmailResponse.verificationStatus", e => ([ // Replace Function
            "unknown",
            "newBusiness",
            "renewal",
            "alreadyRenewed",
            "renewalTooEarly",
            "policyNotYetInEffect",
            "lapseInCoverage"
        ][e || 0])],
        isLapseInCoverage: "verifyEmailResponse.isLapseInCoverage"
    }

    /* ----- Register ----- */

    public participant = {
        prefix: ["prefix", e => e ? e.items.filter(i => i.label === e.value).map(i => e.items.indexOf(i) + 1)[0] : null],
        firstName: "firstName.value",
        middleName: "middleName.value",
        lastName: "lastName.value",
        suffix: ["suffix", e => e ? e.items.filter(i => i.label === e.value).map(i => e.items.indexOf(i) + 1)[0] : null],
        businessName: "businessName.value",
        numberOfEmployees: ["numberOfEmployees.value", e => e ? parseInt(e) : null],
        email: "email.value",
        phone: ["phone.value", e => e ? e.replace(/[\ \-\)\(]/g, "") : null],
        fax: ["fax.value", e => e ? e.replace(/[\ \-\)\(]/g, "") : null],
        addressLine1: "addressLine1.value",
        addressLine2: "addressLine2.value",
        city: "city.value",
        zipCode: ["zipCode.value", e => e ? e.toString() : null],
        stateId: ["state", e => e ? e.items.filter(i => i.label === e.value).map(i => i.data.stateId.toString())[0] : null],
        country: ["countries", e => e ? e[0].name : null],
        countryId: ["countries", e => e ? e[0].countryId.toString() : null],
        renewal: "renewal",
        income: "65.value"
    }

    public formValidationRequest = {
        participant: "participant",
        programEnrollmentId: "programEnrollmentId",
        programEnrollmentRatingItems: "selectedCoverages",
        renewal: "renewal",
        submittedBy: "applicantName.value"
    }

    public formValidationProps = {
        isReferral: "formValidationResponse.referralResponse.isReferral",
        itemSummaries: "formValidationResponse.itemSummaries",
        paymentTypes: FORM_VALIDATION_PAYMENT_TYPES,
        allowCreditCard: [FORM_VALIDATION_PAYMENT_TYPES, e => !!(e||[]).filter(type => type.paymentTypeId === CREDIT_CARD_PAYMENT_TYPE_ID && !type.disabled).length],
        allowAch: [FORM_VALIDATION_PAYMENT_TYPES, e => !!(e||[]).filter(type => type.paymentTypeId === ACH_PAYMENT_TYPE_ID && !type.disabled).length],
        // Payment
        premium: ["formValidationResponse.premium", e => forceAdd(e, this.util.get("formValidationResponse.premiumAdjustment"))],
        total: "formValidationResponse.total",
        agencyFee: "formValidationResponse.agencyFee",
        taxes: "formValidationResponse.adjustedTaxes",
        fees: "formValidationResponse.adjustedFees",
        surcharge: "formValidationResponse.surcharge",
        surchargeType: ["formValidationResponse.surchargeType", e => ([
            null,
            "NJ PLIGA Surcharge" // "CFEE"
        ])[e || 0]],
        allowPremiumFinance: "formValidationResponse.allowPremiumFinance",
        adjustedPremium: "formValidationResponse.adjustedPremium",
        paymentAmount: ["formValidationResponse.total", e => {
            var adjustedPremium = this.util.get("formValidationResponse.adjustedPremium") || 0;
            if (adjustedPremium > e) e = adjustedPremium;
            return this.setItemValue("paymentAmount", toCurrency(e));
        }]
    }

    public additionalInfoRequest = {
        firstName: "firstName.value",
        lastName: "lastName.value",
        address: "addressLine1.value",
        city: "city.value",
        state: "state.value", // may need different value
        zip: "zipCode.value",
        phone: ["phone.value", e => e ? e.replace(/[\ \-\)\(]/g, "") : null],
        email: "email.value",
        isRenewal: "renewal",
        policyNumber: "policyNumber.value",
        policyExpirationDate: ["policyExpirationDate.value", policyExpirationDate => {
            if (!policyExpirationDate) return null;
            if (policyExpirationDate.indexOf("/") > -1) {
                var split = policyExpirationDate.split("/").map(function (e) { return parseInt(e); });
                var month = split[0] > 10 ? split[0] : "0" + split[0];
                var day = split[1] > 10 ? split[1] : "0" + split[1];
                var year = split[2];
                policyExpirationDate = year + "-" + month + "-" + day;
            }
            return policyExpirationDate;
        }]
    }

    /* ----- Payment ----- */

    public appliedPayEnabledProps = {
        appliedPayEnabled: ["appliedPayEnabledResponse", e => e === true]
    }

    public saveQuoteRequest = {
        Participant: ["participant", e => (e || this.util.get(this.participant))],
        ParticipantRatingStatusId: 9, // 1 is prospect, 9 is save quote
        ProgramEnrollmentId: "programEnrollmentId",
        ProgramEnrollmentRatingItems: "selectedCoverages",
        Renewal: "renewal",
        QuoteDescription: "quoteDescription.value"
    }

    public premiumFinanceRequest = {
        Address: ["addressLine1.value", e => (e || this.util.get("participant.addressLine1"))],
        ApplicationCode: "applicationCode",
        City: ["city.value", e => (e || this.util.get("participant.city"))],
        DateOfBirth: ["dateOfBirth.value", e => new Date(e)],
        DownPaymentAmount: ["", () => 0],                                                       // might be used
        EarnedTaxesFees: ["", () => 0],                                                         // might be used
        Email: ["email.value", e => (e || this.util.get("participant.email"))],
        FinancedTaxesFees: ["taxes", e => forceAdd(e, this.util.get("fees"))],
        FirstName: ["firstName.value", e => (e || this.util.get("participant.firstName"))],
        LastName: ["lastName.value", e => (e || this.util.get("participant.lastName"))],
        Phone: ["phone.value", e => (e || this.util.get("participant.phone"))],
        PolicyEffectiveDate: ["", e => this.dateToday()],
        PolicyNumber: ["", e => "VL-123456789"],                                                // required but not used
        PostalCode: ["zipCode.value", e => (e || this.util.get("participant.zipCode"))],
        //PurePremium: "premium",
        PurePremium: ["premium", e => forceAdd(e, this.util.get("agencyFee"))],
        StateCode: ["state", e => {
            if (!e || !e.value) return this.util.get("participant.state");
            return e.items.filter(i => i.label === e.value).map(i => i.data.stateCode)[0]
        }],
        TaxId: "socialSecurityNumber.value"
    }

    public premiumFinanceProps = {
        quoteNumber: "premiumFinanceResponse.quoteInformation.quoteNumber",
        totalAmountDue: "premiumFinanceResponse.quoteInformation.totalAmountDue",
        amountFinanced: "premiumFinanceResponse.quoteInformation.amountFinanced",
        installmentAmount: "premiumFinanceResponse.quoteInformation.installmentAmount",
        numberOfInstallments: "premiumFinanceResponse.quoteInformation.numberOfInstallments",
        apr: "premiumFinanceResponse.quoteInformation.apr",
        downPayment: "premiumFinanceResponse.quoteInformation.downPayment",
        // Premium Finance Quote
        pfPdfAgreement: "premiumFinanceResponse.pdfAgreement",
        pfQuoteNumber: ["premiumFinanceResponse.quoteInformation.quoteNumber", e => this.setItemLabel("pfQuoteNumber", e)],
        pfTotal: ["premiumFinanceResponse.quoteInformation.totalPremium", e => this.setItemLabel("pfTotal", toCurrency(e))],
        pfAmountFinanced: ["premiumFinanceResponse.quoteInformation.amountFinanced", e => this.setItemLabel("pfAmountFinanced", toCurrency(e))],
        pfInstallmentAmount: ["premiumFinanceResponse.quoteInformation.installmentAmount", e => this.setItemLabel("pfInstallmentAmount", toCurrency(e))],
        pfNumberOfInstallments: ["premiumFinanceResponse.quoteInformation.numberOfInstallments", e => this.setItemLabel("pfNumberOfInstallments", e)],
        pfApr: ["premiumFinanceResponse.quoteInformation.apr", e => this.setItemLabel("pfApr", e + "%")],
        pfDownPayment: ["premiumFinanceResponse.quoteInformation.downPayment", e => this.setItemLabel("pfDownPayment", toCurrency(e))]
    }

    public participantEnrollmentRequest = {
        Participant: "participant",
        ParticipantRatingStatusId: 1, // 1 is prospect, 9 is save quote, 11 is payment error
        ProgramEnrollmentId: "programEnrollmentId",
        ProgramEnrollmentRatingItems: "selectedCoverages",
        Renewal: "renewal"
    }

    public participantEnrollmentProps = {
        participantRatingId: "participantEnrollmentResponse.participantRatingId",
        paymentSessionToken: "participantEnrollmentResponse.paymentSessionToken"
    }

    public appliedPayInitiateRequest = {
        ParticipantRatingId: "participantRatingId",
        PaymentSessionToken: "paymentSessionToken",
        CaptchaToken: "captchaToken"
    }

    public paymentErrorRequest = {
        ParticipantRatingId: "participantRatingId",
        ParticipantRatingStatusId: 11,
        PaymentTransactionId: TRANSACTION_NUMBER_VALUE,
        PaymentTypeId: ["paymentType.value", e => (e === "creditcard" ? 2 : e === "ach" ? 1 : 0)]
    }
    
    public paymentRequest = {
        // Application Information
        ApplicationCode: "applicationCode",
        CustomerPhoneNumber: "participant.phone",
        ExternalSystemId: "epicCode",
        IncludeReceiptInResponse: true,
        NotifyCustomerEmail: "participant.email",
        ParticipantRatingId: "participantRatingId",
        ProgramOverride: "applicationCode",
        StructureCode: "structureCode",
        PaymentSessionToken: "paymentSessionToken",
        // Payment Information
        FirstName: "paymentFirstName.value",
        LastName: "paymentLastName.value",
        PaymentAmount: ["paymentType.value", e => this.util.get(e === "premiumFinance" ? "downPayment" : "total")],
        // Credit Card
        CreditCardNumber: "creditCardNumber.value",
        ExpirationDate: ["expirationDate.value", e => !e ? null : e.split("/").join("")],
        VerificationCode: "verificationCode.value",
        ZipCode: ["paymentZipCode.value", e => !e ? null : parseInt(e)],
        // ACH
        BankAccountOwner: ["paymentFirstName.value", "paymentLastName.value", (a, b) => `${a} ${b}`],
        AccountNumber: "accountNumber.value",
        RoutingNumber: "routingNumber.value",
        IsChecking: ["bankAccountType.value", e => e === "checkingAccount"],
        IsPersonalAccount: ["bankClientType.value", e => e === "personalAccount"],
    }

    public paymentProps = {
        paymentMessage: ["paymentResponse.message", e => this.setItemValue("paymentMessage", e)],
        confirmationNumber: ["paymentResponse.confirmationNumber", e => this.setItemValue("confirmationNumber", e)],
        receiptEmail: ["participant.email", e => this.setItemValue("receiptEmail", e)],
        transactionNumber: ["paymentResponse.transactionNumber", e => this.setItemValue("transactionNumber", e)]
    }

    public postingStatusRequest = {
        PaidAmount: ["paymentType.value", e => this.util.get(e === "premiumFinance" ? "downPayment" : "total")],
        ParticipantRatingId: "participantRatingId",
        ParticipantRatingStatusId: 2,
        PaymentTransactionId: TRANSACTION_NUMBER_VALUE,
        PaymentTypeId: ["paymentType.value", e => (e === "creditcard" ? 2 : e === "ach" ? 1 : 0)]
    }

    public paymentReceiptRequest = {
        ParticipantRatingId: "participantRatingId",
        TransactionNumber: TRANSACTION_NUMBER_VALUE,
        PaymentSessionToken: "paymentSessionToken"
    }

    public postPayCorrespondenceRequest = {
        PaymentSessionToken: "paymentSessionToken"
    }

    /* ----- Middleware ----- */

    setItemValue(itemId: string, value: any) {
        let item = this.util.get(itemId);
        if (!item) return;
        item.value = value;
        return item;
    }

    setItemLabel(itemId: string, label: string) {
        let item = this.util.get(itemId);
        if (!item) return;
        item.label = label;
        return item;
    }

    dateToday() {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return new Date(year, month, day);
    }

}
